import type { Rng } from './rng';
import { defaultRng, createRng } from './rng';
import {
  generateInvoiceNumber,
  generateUaeTrn,
} from './primitives';
import { COMPANIES, PEOPLE, type SampleCompany } from './uae';

export type VatRate = 5 | 0 | 'EXEMPT';
export type MlsStatus = 'AP' | 'AB' | 'RE' | 'PENDING';
export type InvoiceDirection = 'OUTBOUND' | 'INBOUND';

export interface SampleLineItem {
  description: string;
  descriptionAr?: string;
  quantity: number;
  unitPrice: number;
  vatRate: VatRate;
  /** Computed lineNet = qty * unitPrice (rounded to 2 decimals). */
  lineNet: number;
  /** Computed lineVat (only non-zero for vatRate === 5). */
  lineVat: number;
}

export interface InvoiceAttachment {
  /** Display name (with extension). */
  name: string;
  /** Bytes (UI shows a humanised KB/MB label). */
  sizeBytes: number;
  /** MIME type — restricted to PDF / JPG / PNG per spec § Modul 2. */
  mime: 'application/pdf' | 'image/jpeg' | 'image/png';
  /** Backend storage path. Mock — would point to MinIO/S3 in production. */
  path: string;
}

export interface SampleInvoice {
  invoiceId: string;
  invoiceNumber: string;
  tenantId: string;
  sellerTrn: string;
  sellerName: string;
  buyerTrn: string;
  buyerName: string;
  buyerNameAr: string;
  issueDate: string;
  deliveryDate: string | null;
  currency: 'AED';
  lineItems: SampleLineItem[];
  totalNetAmount: number;
  totalVatAmount: number;
  totalGrossAmount: number;
  mlsStatus: MlsStatus;
  direction: InvoiceDirection;
  /** Idempotency-Key as accepted by Solara's API. */
  idempotencyKey: string;
  /**
   * Optional supporting documents (PDF/JPG/PNG). Per spec § 2 Modul:
   * max 5 attachments, ≤ 10 MB each.
   */
  attachments: InvoiceAttachment[];
}

/** Industry-keyed catalogue of plausible UAE B2B line-item descriptions. */
const ITEM_CATALOG: Record<SampleCompany['industry'], { en: string; ar: string; minQty: [number, number]; price: [number, number] }[]> = {
  trading: [
    { en: 'Wholesale electronics consignment', ar: 'إرسالية إلكترونيات بالجملة', minQty: [10, 200], price: [120, 1200] },
    { en: 'Import duty handling fee',          ar: 'رسوم تخليص جمركي',           minQty: [1, 5],    price: [800, 4500] },
    { en: 'Container demurrage',                ar: 'رسوم تأخير الحاويات',         minQty: [1, 3],    price: [1500, 6000] },
  ],
  logistics: [
    { en: 'Warehouse storage (per pallet/month)', ar: 'تخزين في المستودع للوحدة', minQty: [10, 500], price: [60, 220] },
    { en: 'Last-mile delivery (within Dubai)',     ar: 'توصيل داخل دبي',           minQty: [1, 50],    price: [25, 90] },
    { en: 'Customs brokerage retainer',            ar: 'أتعاب التخليص الجمركي',     minQty: [1, 1],     price: [2500, 8000] },
  ],
  real_estate: [
    { en: 'Office lease (sqft / month)',  ar: 'إيجار مكتب للقدم المربع شهريًا', minQty: [200, 5000], price: [80, 220] },
    { en: 'Service charge — Q1 2026',     ar: 'رسوم الخدمات — الربع الأول',     minQty: [1, 1],      price: [12000, 75000] },
    { en: 'Brokerage commission',          ar: 'عمولة الوساطة',                  minQty: [1, 1],      price: [15000, 110000] },
  ],
  banking: [
    { en: 'Trade finance arrangement fee', ar: 'رسوم ترتيب تمويل تجاري', minQty: [1, 1], price: [5000, 45000] },
    { en: 'Working capital facility setup', ar: 'إعداد تسهيلات رأس المال العامل', minQty: [1, 1], price: [12000, 80000] },
    { en: 'Treasury advisory retainer',     ar: 'أتعاب الاستشارات الخزينية',     minQty: [1, 1], price: [9500, 28000] },
  ],
  energy: [
    { en: 'Industrial diesel — bulk',   ar: 'ديزل صناعي — بالجملة', minQty: [1000, 15000], price: [3.2, 4.1] },
    { en: 'Electrical maintenance retainer', ar: 'صيانة كهربائية — أتعاب', minQty: [1, 1],   price: [22000, 65000] },
    { en: 'LPG cylinder refill (kg)',     ar: 'إعادة تعبئة أسطوانات الغاز', minQty: [50, 800], price: [3.6, 4.2] },
  ],
  telecom: [
    { en: 'Dedicated fibre 1 Gbps',       ar: 'ألياف ضوئية مخصصة 1 جيجابت', minQty: [1, 1],  price: [4500, 9500] },
    { en: 'Mobile fleet plan (per line)', ar: 'باقة شركات للهواتف',          minQty: [10, 500], price: [120, 320] },
    { en: 'Cloud PBX subscription',        ar: 'اشتراك PBX سحابي',            minQty: [1, 1],  price: [3500, 14000] },
  ],
  aviation: [
    { en: 'In-flight catering, regional', ar: 'تموين طيران إقليمي', minQty: [50, 600], price: [55, 140] },
    { en: 'Ground handling — turnaround',  ar: 'مناولة أرضية — رحلة', minQty: [1, 8],     price: [3200, 9500] },
    { en: 'Aircraft maintenance (hours)',  ar: 'صيانة الطائرات بالساعة', minQty: [4, 60], price: [950, 3500] },
  ],
  hospitality: [
    { en: 'Hotel stay (room-night)',     ar: 'إقامة فندقية لليلة',     minQty: [1, 30],  price: [350, 2800] },
    { en: 'F&B catering service',         ar: 'خدمة تموين الطعام',     minQty: [25, 400], price: [85, 320] },
    { en: 'Conference hall rental (day)', ar: 'إيجار قاعة مؤتمرات يومي', minQty: [1, 4],  price: [8000, 38000] },
  ],
  tech: [
    { en: 'SaaS subscription (per seat)', ar: 'اشتراك SaaS لكل مقعد',     minQty: [5, 1000], price: [80, 520] },
    { en: 'Cloud infrastructure usage',    ar: 'استخدام البنية التحتية السحابية', minQty: [1, 1],   price: [4500, 38000] },
    { en: 'On-prem support contract',       ar: 'عقد دعم محلي',                  minQty: [1, 1],   price: [12000, 65000] },
  ],
  retail: [
    { en: 'Retail POS hardware bundle',  ar: 'أجهزة نقاط البيع للتجزئة', minQty: [1, 50], price: [1500, 6500] },
    { en: 'Branded merchandise lot',      ar: 'بضائع بعلامة تجارية',     minQty: [50, 500], price: [25, 220] },
    { en: 'Visual merchandising service', ar: 'خدمات العرض البصري',      minQty: [1, 5],   price: [8000, 28000] },
  ],
  construction: [
    { en: 'Reinforcement steel (ton)',    ar: 'حديد تسليح بالطن',     minQty: [10, 200], price: [2800, 3600] },
    { en: 'Skilled labour (man-day)',     ar: 'عمالة فنية يومية',     minQty: [50, 800], price: [220, 380] },
    { en: 'Heavy equipment rental (day)', ar: 'تأجير معدات ثقيلة يومي', minQty: [1, 30],  price: [1800, 7500] },
  ],
  healthcare: [
    { en: 'Medical supplies pack',        ar: 'حزمة مستلزمات طبية', minQty: [10, 200], price: [180, 1200] },
    { en: 'Diagnostic services package',  ar: 'باقة خدمات تشخيصية', minQty: [1, 50],   price: [400, 2200] },
    { en: 'Clinical staff retainer',       ar: 'أتعاب طاقم سريري',   minQty: [1, 1],    price: [18000, 55000] },
  ],
};

const round2 = (n: number) => Math.round(n * 100) / 100;

function pseudoUuid(rng: Rng): string {
  const hex = (n: number) => rng.int(0, 15).toString(16).repeat(n);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}

/** Idempotency-Key (printable ASCII, max 128 chars). */
function ikey(rng: Rng): string {
  let s = '';
  for (let i = 0; i < 32; i++) s += rng.int(0, 15).toString(16);
  return s;
}

function isoDateMinus(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

export function generateSampleLineItems(
  industry: SampleCompany['industry'],
  rng: Rng = defaultRng(),
): SampleLineItem[] {
  const choices = ITEM_CATALOG[industry];
  const itemCount = rng.int(1, 4);
  const items: SampleLineItem[] = [];
  for (let i = 0; i < itemCount; i++) {
    const def = rng.pick(choices);
    const qty = rng.int(def.minQty[0], def.minQty[1]);
    const unitPrice = round2(rng.next() * (def.price[1] - def.price[0]) + def.price[0]);
    // 80% standard rate, 15% zero-rate, 5% exempt — UAE distribution-ish.
    const r = rng.next();
    const vatRate: VatRate = r < 0.8 ? 5 : r < 0.95 ? 0 : 'EXEMPT';
    const lineNet = round2(qty * unitPrice);
    const lineVat = vatRate === 5 ? round2(lineNet * 0.05) : 0;
    items.push({
      description: def.en,
      descriptionAr: def.ar,
      quantity: qty,
      unitPrice,
      vatRate,
      lineNet,
      lineVat,
    });
  }
  return items;
}

export function generateSampleInvoice(
  options: {
    rng?: Rng;
    sellerCompany?: SampleCompany;
    sellerTrn?: string;
    tenantId?: string;
    direction?: InvoiceDirection;
    issueDateDaysAgo?: number;
    mlsStatus?: MlsStatus;
  } = {},
): SampleInvoice {
  const rng = options.rng ?? defaultRng();
  const seller = options.sellerCompany ?? rng.pick(COMPANIES);
  const buyer = rng.pick(COMPANIES.filter((c) => c.slug !== seller.slug));

  const lineItems = generateSampleLineItems(seller.industry, rng);
  const totalNet = round2(lineItems.reduce((s, i) => s + i.lineNet, 0));
  const totalVat = round2(lineItems.reduce((s, i) => s + i.lineVat, 0));
  const totalGross = round2(totalNet + totalVat);

  const issueDateDaysAgo = options.issueDateDaysAgo ?? rng.int(1, 28);
  const hasDelivery = rng.chance(0.6);

  const r = rng.next();
  const mls: MlsStatus = options.mlsStatus
    ? options.mlsStatus
    : r < 0.7
    ? 'AP'
    : r < 0.85
    ? 'AB'
    : r < 0.95
    ? 'RE'
    : 'PENDING';

  return {
    invoiceId: pseudoUuid(rng),
    invoiceNumber: generateInvoiceNumber(rng),
    tenantId: options.tenantId ?? pseudoUuid(rng),
    sellerTrn: options.sellerTrn ?? generateUaeTrn(rng),
    sellerName: seller.nameEn,
    buyerTrn: generateUaeTrn(rng),
    buyerName: buyer.nameEn,
    buyerNameAr: buyer.nameAr,
    issueDate: isoDateMinus(issueDateDaysAgo),
    deliveryDate: hasDelivery ? isoDateMinus(Math.max(0, issueDateDaysAgo - rng.int(0, 5))) : null,
    currency: 'AED',
    lineItems,
    totalNetAmount: totalNet,
    totalVatAmount: totalVat,
    totalGrossAmount: totalGross,
    mlsStatus: mls,
    direction: options.direction ?? (rng.chance(0.85) ? 'OUTBOUND' : 'INBOUND'),
    idempotencyKey: ikey(rng),
    attachments: maybeAttachments(rng),
  };
}

/** ~30 % of invoices have 1–3 attachments to keep the demo realistic. */
function maybeAttachments(rng: Rng): InvoiceAttachment[] {
  if (!rng.chance(0.3)) return [];
  const count = rng.int(1, 3);
  const out: InvoiceAttachment[] = [];
  const samples: { name: string; mime: InvoiceAttachment['mime']; min: number; max: number }[] = [
    { name: 'purchase-order.pdf',   mime: 'application/pdf', min: 80_000,    max: 600_000 },
    { name: 'delivery-note.pdf',    mime: 'application/pdf', min: 40_000,    max: 280_000 },
    { name: 'signed-contract.pdf',  mime: 'application/pdf', min: 250_000,   max: 2_400_000 },
    { name: 'goods-photo.jpg',      mime: 'image/jpeg',      min: 600_000,   max: 4_500_000 },
    { name: 'site-photo.jpg',       mime: 'image/jpeg',      min: 800_000,   max: 5_200_000 },
    { name: 'stamp-scan.png',       mime: 'image/png',       min: 120_000,   max: 700_000 },
  ];
  const picks = rng.sample(samples, count);
  for (const s of picks) {
    out.push({
      name: s.name,
      mime: s.mime,
      sizeBytes: rng.int(s.min, s.max),
      path: `s3://solara-portal-uploads/${rng.int(1, 9999)}/${s.name}`,
    });
  }
  return out;
}

/** Deterministic 25 invoices by default (seeded). */
export function generateSampleInvoices(count = 25, seed = 0x1c0a1d): SampleInvoice[] {
  const rng = createRng(seed);
  const invoices: SampleInvoice[] = [];
  for (let i = 0; i < count; i++) {
    const seller = rng.pick(COMPANIES);
    invoices.push(
      generateSampleInvoice({
        rng,
        sellerCompany: seller,
        issueDateDaysAgo: rng.int(0, 60),
        // First handful are most recent so they show up at the top of lists.
        ...(i < 5 ? { mlsStatus: i === 0 ? 'PENDING' : i === 1 ? 'AB' : 'AP' } : {}),
      }),
    );
  }
  // Newest-first ordering for UI fixtures.
  invoices.sort((a, b) => (a.issueDate < b.issueDate ? 1 : a.issueDate > b.issueDate ? -1 : 0));
  return invoices;
}

export interface InvoiceFormSample {
  buyerTrn: string;
  buyerName: string;
  invoiceNumber: string;
  issueDate: string;
  deliveryDate: string | null;
  currency: 'AED';
  lineItems: { description: string; quantity: number; unitPrice: number; vatRate: VatRate }[];
}

/** Sample payload for the "Fill with sample data" button on /new. */
export function generateInvoiceFormSample(
  rng: Rng = defaultRng(),
  industry?: SampleCompany['industry'],
): InvoiceFormSample {
  const buyer = rng.pick(COMPANIES);
  const items = generateSampleLineItems(industry ?? buyer.industry, rng).map((i) => ({
    description: i.description,
    quantity: i.quantity,
    unitPrice: i.unitPrice,
    vatRate: i.vatRate,
  }));
  return {
    buyerTrn: generateUaeTrn(rng),
    buyerName: buyer.nameEn,
    invoiceNumber: generateInvoiceNumber(rng),
    issueDate: new Date().toISOString().slice(0, 10),
    deliveryDate: null,
    currency: 'AED',
    lineItems: items,
  };
}
